import React, { useState } from "react";
import { DateDisplay } from "../components/DateDisplay";
import { DateTimeDisplay } from "../components/DateTimeDisplay";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Stack,
  Alert,
  AlertTitle,
  TextField,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Menu,
  Autocomplete,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { DialogCloseButton } from "../components/DialogCloseButton";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { DatePicker } from "../components/DatePicker";
import {
  useTransactionDetails,
  useBulkUpdateTransactions,
  usePreviewTransactionUpdate,
  useResetTransaction,
  useAutoTransactionRules,
  useActionSets,
  useConfig,
  useImports,
} from "../api/queries";
import {
  coalesceActions,
  fieldChangeToActions,
  type FieldChange,
} from "../logic/edit-actions";
import { FIELD_LABELS } from "../components/RuleEditor";
import { InlineTextEditor } from "../components/InlineTextEditor";
import { useRegistry } from "../context/RegistryContext";
import {
  buildAccountNumberIndex,
  describeTransfer,
  resolveCounterparty,
} from "../logic/counterparty";
import { derivedAccount } from "../logic/derivedAccount";
import { DerivedAccountChip } from "../components/DerivedAccountChip";
import AttachmentPreview from "../components/AttachmentPreview";
import { CenteredSpinner } from "../components/CenteredSpinner";
import { useAttachments } from "../hooks/useAttachments";
import { AttachmentSelectModal } from "../components/AttachmentSelectModal";
import { AttachmentUploadModal } from "../components/AttachmentUploadModal";
import {
  type TransactionDto,
  type EditActionDto,
  type MatchProblemDto,
  type AutoTransactionRuleDto,
  makeDateString,
  type DateString,
  type AutoTxId,
  type CategoryId,
  type Money,
} from "../api/types";
import { makeMoney } from "../logic/money";
import { errorText } from "../api/common";
import { EntityChip, type EntityType } from "../components/EntityChip";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { CopyableText } from "../components/CopyableText";
import { PromptRuleDialog } from "../components/PromptRuleDialog";
import { collectPrompts } from "../components/PromptRuleDialog/prompts";
import { EntityPickerModal } from "../components/EntityPickerField";
import { LinkedInvoice } from "../components/LinkedInvoice";
import { invoicePath } from "../logic/invoice-path";

/**
 * The registries an edit action's value can point into — plus attachments,
 * which aren't registry entities but resolve the same way (id → filename).
 */
type EntityKind =
  | "source"
  | "group"
  | "expenseType"
  | "category"
  | "invoice"
  | "attachment";

/**
 * Resolves a registry entity id to the name the user knows it by. Keyed by kind
 * as well as id: ids are only unique *within* a registry, so a flat map would
 * be free to answer a group question with a source's name.
 */
type EntityNameLookup = (kind: EntityKind, id: string) => string | undefined;

// Human-readable value for a pending edit action. Two shapes need help: the
// money-valued actions carry a `Money` object, so `String(value)` would render
// "[object Object]"; and the entity-valued ones carry *ids* (see `FieldChange`
// in logic/edit-actions.ts, which passes picker ids straight through), so
// without a lookup they render as a bare ULID. An id that no longer resolves —
// a deleted category, a stale pending edit — falls back to itself rather than
// to nothing, so the action stays identifiable.
const renderActionValue = (
  action: EditActionDto,
  entityName: EntityNameLookup,
): string => {
  switch (action.field) {
    case "amount":
    case "split":
      return action.value.amount;
    case "foreignAmount":
      return action.value
        ? `${action.value.amount} ${action.value.currency}`
        : "none";
    case "source":
    case "group":
    case "expenseType":
      return entityName(action.field, action.value) ?? action.value;
    case "addCategory":
    case "removeCategory":
      return action.value
        .map((id) => entityName("category", id) ?? id)
        .join(", ");
    case "invoice":
      return action.value
        ? (entityName("invoice", action.value) ?? action.value)
        : "none";
    case "receipt":
      return action.value
        ? (entityName("attachment", action.value) ?? action.value)
        : "none";
    case "addAttachment":
    case "removeAttachment":
      return entityName("attachment", action.value) ?? action.value;
    default:
      return String(action.value);
  }
};

export type TransactionDetailsPageProps = {
  transactionId?: string;
  onClose?: () => void;
  onCreateRule?: (transaction: TransactionDto) => void;
};

export const TransactionDetailsPage = ({
  transactionId: propId,
  onClose,
  onCreateRule,
}: TransactionDetailsPageProps) => {
  const { id: paramId } = useParams<{ id: string }>();
  const id = propId || paramId;
  const { data: attachmentsList } = useAttachments();

  const navigate = useNavigate();
  const { data, isLoading, error } = useTransactionDetails(id);
  const { data: rules } = useAutoTransactionRules();
  const actionSets = useActionSets();
  const { data: config } = useConfig();
  const { data: imports } = useImports();
  const {
    sources,
    groups,
    categories,
    expenseTypes,
    invoices,
    categoryMap,
    withHidden,
  } = useRegistry();
  const updateTransactions = useBulkUpdateTransactions();
  const resetTransaction = useResetTransaction();
  const previewMutation = usePreviewTransactionUpdate();

  const [pendingActions, setPendingActions] = useState<EditActionDto[]>([]);
  const [previewDetails, setPreviewDetails] = useState<TransactionDto | null>(
    null,
  );
  /// Dispatches the pending actions fall through — the cheapest place to
  /// notice one is before the edit is saved at all.
  const [previewProblems, setPreviewProblems] = useState<MatchProblemDto[]>([]);
  const [pendingPromptRule, setPendingPromptRule] =
    useState<AutoTransactionRuleDto | null>(null);
  const [editingField, setEditingField] = useState<keyof TransactionDto | null>(
    null,
  );
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [isApplyRuleDialogOpen, setIsApplyRuleDialogOpen] = useState(false);
  const [isConfirmSaveDialogOpen, setIsConfirmSaveDialogOpen] = useState(false);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [isReceiptUploadOpen, setIsReceiptUploadOpen] = useState(false);
  const [suggestionAnchorEl, setSuggestionAnchorEl] =
    useState<null | HTMLElement>(null);

  const matchingRules = React.useMemo(() => {
    if (!data || !rules) return [];
    const byId = new Map(rules.map((r) => [r.id, r] as const));
    return data.matchingRules
      .map((info) => byId.get(info.id))
      .filter((r): r is NonNullable<typeof r> => Boolean(r));
  }, [data, rules]);

  const ruleNamesById = React.useMemo(() => {
    const map: Record<string, string> = {};
    rules?.forEach((r) => {
      map[r.id] = r.name;
    });
    return map;
  }, [rules]);

  // The rule a pending action applies, for the pending lists; `null` for a
  // plain field edit. An invoice rule is posted as its actions, led by the
  // marker that records it — that marker is the row that names the rule.
  const pendingRuleName = (action: EditActionDto): string | null => {
    if (action.field === "applyAutoTx")
      return ruleNamesById[action.value] ?? action.value;
    if (action.field === "invoiceRuleApplied")
      return `Invoice: ${action.value.name}`;
    return null;
  };

  // Name lookup for the entity-valued edit actions, one registry per kind.
  // Attachments come from the attachments query rather than the registry; the
  // filename is the name the user knows one by.
  const entityName = React.useCallback<EntityNameLookup>(
    (kind, entityId) => {
      if (kind === "attachment") {
        return attachmentsList?.find((a) => a.id === entityId)?.filename;
      }
      const registries: Record<
        Exclude<EntityKind, "attachment">,
        { id: string; name: string }[]
      > = {
        source: sources,
        group: groups,
        expenseType: expenseTypes,
        category: categories,
        invoice: invoices,
      };
      return registries[kind].find((e) => e.id === entityId)?.name;
    },
    [sources, groups, categories, expenseTypes, invoices, attachmentsList],
  );

  // History rows arrive with raw ids for the id-typed fields (the backend
  // recomputes them from stored edit actions); resolve them to names the same
  // way pending actions are.
  const historyValue = React.useCallback(
    (field: string, value: string): string => {
      if (field === "receiptId") {
        return entityName("attachment", value) ?? value;
      }
      if (field === "invoice") {
        return entityName("invoice", value) ?? value;
      }
      return value;
    },
    [entityName],
  );

  const refreshPreview = React.useCallback(
    (actions: EditActionDto[]) => {
      if (!data) return;
      if (actions.length === 0) {
        setPreviewDetails(null);
        setPreviewProblems([]);
        return;
      }
      previewMutation.mutate(
        { id: data.id, actions },
        {
          onSuccess: (preview) => {
            setPreviewDetails(preview.details);
            setPreviewProblems(preview.problems);
          },
        },
      );
    },
    [data, previewMutation],
  );

  // `refreshPreview` stays outside the state updater: an updater has to be
  // pure, and firing the request from inside one means StrictMode's double
  // invoke sends it twice. Mirrors `useTransactionListState.appendActions`.
  const appendActions = (newActions: EditActionDto[]) => {
    const combined = coalesceActions(pendingActions, newActions);
    setPendingActions(combined);
    refreshPreview(combined);
  };

  const handleOpenSuggestions = (event: React.MouseEvent<HTMLElement>) => {
    setSuggestionAnchorEl(event.currentTarget);
  };

  const handleCloseSuggestions = () => {
    setSuggestionAnchorEl(null);
  };

  const handleApplyRule = (rule: AutoTransactionRuleDto) => {
    if (!data) return;
    handleCloseSuggestions();
    // Expanded, so a prompt inside a cited action set opens the dialog too.
    const hasPrompts = collectPrompts(rule, actionSets.data).length > 0;
    if (hasPrompts) {
      setPendingPromptRule(rule);
      return;
    }
    // Virtual (invoice) rules aren't stored, so the server can't resolve
    // `applyAutoTx(id)`. Apply their concrete actions directly instead.
    if (!rule.editable) {
      appendActions(rule.actions);
      return;
    }
    appendActions([{ field: "applyAutoTx", value: rule.id as AutoTxId }]);
  };

  const effectiveTx: TransactionDto | undefined = previewDetails ?? data;

  // Own-accounts indexed by account number, for resolving a counter-account.
  // A hook, so it lives above the early returns; the resolution itself happens
  // below, where `transaction` is in scope.
  // Hidden accounts included: a transfer to one still names it.
  const accountNumberIndex = React.useMemo(
    () => buildAccountNumberIndex(withHidden.sources),
    [withHidden.sources],
  );

  const pendingEdits = React.useMemo(() => {
    if (!data || !effectiveTx) return {};
    const diff: Partial<TransactionDto> = {};
    for (const k in effectiveTx) {
      const key = k as keyof TransactionDto;
      if (
        key === "id" ||
        key === "origin" ||
        key === "appliedRules" ||
        // The preview response never repopulates matchingRules, so comparing
        // it against the fetched details manufactures a phantom pending edit.
        key === "matchingRules" ||
        key === "lastUpdated" ||
        key === "revision" ||
        key === "dateAdded"
      ) {
        continue;
      }
      const a = effectiveTx[key];
      const b = data[key];
      // Value comparison (not reference): `amount`/`foreignAmount` are now
      // `Money` objects, and arrays/objects are fresh instances after a preview
      // re-parse, so `===` would report spurious changes.
      const equal = JSON.stringify(a) === JSON.stringify(b);
      if (!equal) {
        // @ts-expect-error Types are safe but TypeScript fails to infer the specific key mapping
        diff[key] = a;
      }
    }
    return diff;
  }, [data, effectiveTx]);

  if (isLoading) return <CenteredSpinner />;
  if (error)
    return <Alert severity="error">Error: {(error as Error).message}</Alert>;
  if (!data) return <Alert severity="warning">Transaction not found</Alert>;

  const { history, problems, ...transaction } = data;
  const isSplit = transaction.id ? !transaction.id.endsWith("00") : false;

  // The counter-account, when it is one of your own accounts. Derived rather
  // than stored: the row already carries `payToAccount` and the registry
  // carries each account's own number, so this cannot drift out of step with
  // the registry the way a stored copy would.
  //
  // Resolved from `transaction` — the same object the number below is rendered
  // from, so the two can never disagree. `payToAccount` is import-sourced and
  // read-only, so a preview never changes it. The *direction* reads from
  // `effectiveTx` instead, because amount, source and merchant are all
  // editable and an unsaved sign flip should turn the arrow around.
  const counterparty = resolveCounterparty(
    transaction.payToAccount,
    accountNumberIndex,
  );
  // Only worth showing when both ends are yours: for an external payee the
  // source and merchant already say the same thing.
  // `effectiveTx` is `previewDetails ?? data` and `data` is non-null by here,
  // but that narrowing doesn't reach a const computed above the early returns.
  const shown = effectiveTx ?? transaction;
  const transfer = counterparty
    ? describeTransfer(shown.amount, shown.source, shown.merchant, counterparty)
    : undefined;
  // The details DTO carries no derived account, so the page derives it with
  // the server's rule. Read from `shown` like the direction above, so an
  // unsaved source or expense-type edit is reflected in what derives.
  const account = derivedAccount(
    sources.find((s) => s.name === shown.source)?.kind,
    shown.expenseType,
    counterparty,
  );

  const invoiceSupported = (() => {
    const { origin } = transaction;
    if (origin.type !== "Import") return true;
    if (!config || !imports) return true;
    const importItem = imports.find((i) => i.id === origin.value);
    if (!importItem) return true;
    const importerConfig = config.importConfigs.find(
      (c) => c.name === importItem.importerName,
    );
    return importerConfig?.hasInvoice ?? true;
  })();
  // Read-only: an invoice rule or an import sets it. Taken from the preview so
  // a pending invoice rule shows the invoice it would link.
  const invoiceChanged = "invoice" in pendingEdits;
  const linkedInvoice = invoiceChanged
    ? pendingEdits.invoice
    : transaction.invoice;

  const handleReset = () => {
    if (id) {
      // The reset supersedes whatever the last save failed with; leaving that
      // banner up would report a failure that is no longer true.
      updateTransactions.reset();
      resetTransaction.mutate(id, {
        onSuccess: () => setIsResetDialogOpen(false),
      });
    }
  };

  const handleEditClick = (field: keyof TransactionDto) => {
    setEditingField(field);
  };

  const handleChange = (change: FieldChange) => {
    if (!effectiveTx) return;
    const newActions = fieldChangeToActions(
      change,
      effectiveTx,
      (name) => categoryMap[name]?.id,
    );
    if (newActions.length === 0) return;
    appendActions(newActions);
  };

  const handleCancel = () => {
    updateTransactions.reset();
    setPendingActions([]);
    setPreviewDetails(null);
    setPreviewProblems([]);
    setEditingField(null);
  };

  const handleSave = () => {
    // Drop whatever the last attempt failed with, so a stale reason doesn't
    // greet a save that hasn't been tried yet.
    updateTransactions.reset();
    setIsConfirmSaveDialogOpen(true);
  };

  const handleReceiptSave = (selectedIds: string[]) => {
    handleChange({
      field: "receiptId",
      value: (selectedIds.length > 0 ? selectedIds[0] : null) as
        | import("../api/types").AttachmentId
        | null,
    });
    setIsReceiptModalOpen(false);
    setEditingField(null);
  };

  const confirmSave = () => {
    if (!id || pendingActions.length === 0) return;

    updateTransactions.mutate([{ id, actions: pendingActions }], {
      onSuccess: () => {
        setPendingActions([]);
        setPreviewDetails(null);
        setPreviewProblems([]);
        setEditingField(null);
        setIsConfirmSaveDialogOpen(false);
      },
    });
  };

  const renderField = (field: keyof TransactionDto) => {
    const isEditing = editingField === field;
    const hasPendingChange = pendingEdits[field] !== undefined;
    const value = hasPendingChange ? pendingEdits[field] : transaction[field];

    if (isEditing) {
      if (field === "datePaid") {
        return (
          <DatePicker
            mode="single"
            label="Date Paid"
            required
            fullWidth
            defaultOpen
            onClose={() => setEditingField(null)}
            value={(value as string) || null}
            onChange={(v) => {
              if (v) handleChange({ field, value: makeDateString(v) });
            }}
          />
        );
      }
      if (field === "amount") {
        const money = value as Money;
        return (
          <InlineTextEditor
            type="number"
            step="0.01"
            aria-label="Amount"
            initialValue={money.amount}
            onCommit={(v) =>
              handleChange({ field, value: makeMoney(v, money.currency) })
            }
            onClose={() => setEditingField(null)}
          />
        );
      }
      if (field === "merchant") {
        return (
          <InlineTextEditor
            aria-label="Merchant"
            initialValue={value as string}
            onCommit={(v) => handleChange({ field, value: v })}
            onClose={() => setEditingField(null)}
          />
        );
      }
      if (field === "description") {
        return (
          <InlineTextEditor
            multiline
            aria-label="Description"
            initialValue={value as string}
            onCommit={(v) => handleChange({ field, value: v })}
            onClose={() => setEditingField(null)}
          />
        );
      }
      if (field === "source") {
        const id = sources.find((s) => s.name === value)?.id ?? null;
        return (
          <EntityPickerModal
            kind="source"
            open
            onClose={() => setEditingField(null)}
            value={id}
            onChange={(newId) => {
              if (newId) handleChange({ field, value: newId });
            }}
          />
        );
      }
      if (field === "group") {
        const id = groups.find((g) => g.name === value)?.id ?? null;
        return (
          <EntityPickerModal
            kind="group"
            open
            onClose={() => setEditingField(null)}
            value={id}
            onChange={(newId) => {
              if (newId) handleChange({ field, value: newId });
            }}
          />
        );
      }

      if ((field as string) === "receiptId") {
        return (
          <Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setIsReceiptModalOpen(true)}
              >
                Select Receipt
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setIsReceiptUploadOpen(true)}
              >
                Upload new
              </Button>
            </Stack>
            <AttachmentSelectModal
              open={isReceiptModalOpen}
              onClose={() => {
                setIsReceiptModalOpen(false);
                setEditingField(null);
              }}
              onSave={handleReceiptSave}
              availableAttachments={attachmentsList || []}
              initialSelectedIds={value ? [value as string] : []}
              selectionMode="single"
            />
            <AttachmentUploadModal
              open={isReceiptUploadOpen}
              onClose={() => {
                setIsReceiptUploadOpen(false);
                setEditingField(null);
              }}
              onUploadSuccess={(newAttachment) => {
                handleReceiptSave([newAttachment.id]);
                setIsReceiptUploadOpen(false);
              }}
            />
          </Box>
        );
      }

      if (field === ("expenseType" as keyof TransactionDto)) {
        const id =
          expenseTypes.find((e) => e.name === (value as string))?.id ?? null;
        return (
          <EntityPickerModal
            kind="expense-type"
            open
            onClose={() => setEditingField(null)}
            value={id}
            onChange={(newId) => {
              if (newId) handleChange({ field: "expenseType", value: newId });
            }}
          />
        );
      }
      if (field === "categories") {
        const cats = (value as string[]) || [];
        const ids = cats
          .map((name) => categories.find((c) => c.name === name)?.id)
          .filter((id): id is NonNullable<typeof id> => id !== undefined);
        return (
          <EntityPickerModal<CategoryId>
            kind="category"
            multiple
            open
            onClose={() => setEditingField(null)}
            value={ids}
            onChange={(newIds) =>
              handleChange({ field: "categories", value: newIds })
            }
          />
        );
      }
    }

    // Display mode
    const displayContent = () => {
      if (field === "datePaid") {
        return <DateDisplay date={value as DateString} />;
      }
      if (field === "amount") {
        const val = value as Money;
        return (
          <>
            {val.amount}
            {field === "amount" && transaction.foreignAmount && (
              <Typography
                component="span"
                variant="body2"
                sx={{
                  color: "text.secondary",
                  ml: 1,
                }}
              >
                ({transaction.foreignAmount.amount}{" "}
                {transaction.foreignAmount.currency})
              </Typography>
            )}
          </>
        );
      }
      if (field === "source" || field === "group" || field === "expenseType") {
        return (
          <EntityChip
            type={field as EntityType}
            name={value as string}
            variant="filled"
          />
        );
      }

      if ((field as string) === "receiptId") {
        if (!value) {
          return (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              None
            </Typography>
          );
        }
        const attachmentId = value as string;
        const attachmentMetadata = attachmentsList?.find(
          (a) => a.id === attachmentId,
        );
        if (!attachmentMetadata) return null;
        return (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            {/* Stop propagation so tapping the receipt opens the viewer
                instead of flipping the field into edit mode — that would
                unmount this subtree and destroy the dialog as it opens. */}
            <Box onClick={(e) => e.stopPropagation()} sx={{ display: "flex" }}>
              <AttachmentPreview attachment={attachmentMetadata} />
            </Box>
            <Button size="small" onClick={() => handleEditClick(field)}>
              Change
            </Button>
          </Stack>
        );
      }

      if (field === "categories") {
        const cats = (value as string[]) || [];
        if (cats.length === 0)
          return (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              None
            </Typography>
          );
        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {cats.map((catName) => (
              <EntityChip
                key={catName}
                type="category"
                name={catName}
                size="small"
              />
            ))}
          </Box>
        );
      }
      return (
        <Typography variant={field === "description" ? "body1" : "body1"}>
          {value as string}
        </Typography>
      );
    };

    return (
      <Box
        onClick={() => handleEditClick(field)}
        sx={{
          cursor: "pointer",
          p: 0.5,
          borderRadius: 1,
          border: "1px solid transparent",
          "&:hover": {
            border: "1px dashed",
            borderColor: "text.secondary",
          },
          bgcolor: hasPendingChange ? "action.selected" : undefined,
        }}
      >
        {displayContent()}
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", p: 2 }}>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        {onClose ? (
          <DialogCloseButton onClick={onClose} />
        ) : (
          <Button
            component={Link}
            to="/transactions"
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        )}
        {/* Gated on the actions too: a rule that applies cleanly but changes
            no field (a match with no arm for this transaction, or values the
            transaction already has) queues an action with an empty field
            diff, and it still needs a way to be saved or dropped. */}
        {Object.keys(pendingEdits).length > 0 || pendingActions.length > 0 ? (
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" color="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={updateTransactions.isPending}
            >
              {updateTransactions.isPending ? "Saving..." : "Save"}
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={1}>
            <Tooltip
              title={
                isSplit
                  ? "Only base transactions can be reset"
                  : "Reset transaction to initial state"
              }
            >
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setIsResetDialogOpen(true)}
                  disabled={isSplit}
                >
                  Reset
                </Button>
              </span>
            </Tooltip>
          </Stack>
        )}
        {matchingRules.length > 0 && (
          <Tooltip title="View suggestions">
            <IconButton
              color="primary"
              onClick={handleOpenSuggestions}
              sx={{ ml: 1 }}
            >
              <LightbulbIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Apply Auto Transaction Rule">
          <IconButton
            onClick={() => setIsApplyRuleDialogOpen(true)}
            sx={{ ml: 1 }}
          >
            <AutoFixHighIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Create Auto Transaction Rule">
          <IconButton
            aria-label="Create Auto Transaction Rule"
            onClick={() =>
              onCreateRule
                ? onCreateRule(data)
                : navigate("/admin/auto-transactions/new", {
                    state: { transaction: data },
                  })
            }
            sx={{ ml: 1 }}
          >
            <PlaylistAddIcon />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={
            transaction.isIgnored
              ? "Unignore transaction"
              : "Ignore transaction"
          }
        >
          <IconButton
            color={transaction.isIgnored ? "warning" : "default"}
            onClick={() => {
              updateTransactions.mutate([
                {
                  id: transaction.id,
                  actions: [{ field: "ignore", value: !transaction.isIgnored }],
                },
              ]);
            }}
            disabled={updateTransactions.isPending}
            sx={{ ml: 1 }}
          >
            {transaction.isIgnored ? <VisibilityIcon /> : <VisibilityOffIcon />}
          </IconButton>
        </Tooltip>
      </Stack>
      <Menu
        anchorEl={suggestionAnchorEl}
        open={Boolean(suggestionAnchorEl)}
        onClose={handleCloseSuggestions}
      >
        {matchingRules.map((rule) => (
          <MenuItem key={rule.id} onClick={() => handleApplyRule(rule)}>
            {rule.name}
          </MenuItem>
        ))}
      </Menu>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{
          fontWeight: "bold",
        }}
      >
        Transaction Details
      </Typography>
      {/* A rejected save has to say so. The confirm dialog carries its own
          copy of this — it stays open on failure and covers the page — so this
          one is for the edits that never open it, like the ignore toggle. */}
      {updateTransactions.isError && !isConfirmSaveDialogOpen && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => updateTransactions.reset()}
        >
          <AlertTitle>Saving failed</AlertTitle>
          {errorText(updateTransactions.error)}
        </Alert>
      )}
      {/* A rule reached a match none of whose arms name this transaction.
          Recomputed on every read, so it appears the moment a new source (or
          group) turns up — which is what the match is for. */}
      {problems.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>A rule couldn't decide what to do here</AlertTitle>
          <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
            {problems.map((problem, idx) => (
              <Typography component="li" variant="body2" key={idx}>
                {problem.rule ? (
                  <>
                    <Link
                      to={`/admin/auto-transactions/${problem.rule.id}`}
                      style={{ color: "inherit" }}
                    >
                      {problem.rule.name}
                    </Link>{" "}
                    has no arm for
                  </>
                ) : (
                  "No arm for"
                )}{" "}
                {FIELD_LABELS[problem.field].toLowerCase()}{" "}
                <strong>“{problem.value}”</strong>.
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: "text.secondary",
              }}
            >
              Date Paid
            </Typography>
            {renderField("datePaid")}
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: "text.secondary",
              }}
            >
              Payment Date
            </Typography>
            <Box sx={{ p: 0.5 }}>
              <DateDisplay date={transaction.paymentDate} />
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: "text.secondary",
              }}
            >
              Amount
            </Typography>
            {renderField("amount")}
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: "text.secondary",
              }}
            >
              Merchant
            </Typography>
            {renderField("merchant")}
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: "text.secondary",
              }}
            >
              Description
            </Typography>
            {renderField("description")}
          </Grid>

          <Grid size={{ xs: 12, sm: 3 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                color: "text.secondary",
              }}
            >
              Source
            </Typography>
            {renderField("source")}
          </Grid>

          <Grid size={{ xs: 12, sm: 3 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                color: "text.secondary",
              }}
            >
              Expense Type
            </Typography>
            {renderField("expenseType")}
          </Grid>

          <Grid size={{ xs: 12, sm: 3 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                color: "text.secondary",
              }}
            >
              Account
            </Typography>
            {/* Derived, so there is nothing to edit: no click handler. */}
            <DerivedAccountChip value={account} />
          </Grid>

          <Grid size={{ xs: 12, sm: 3 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                color: "text.secondary",
              }}
            >
              Group
            </Typography>
            {renderField("group")}
          </Grid>

          {transaction.kid && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{
                  color: "text.secondary",
                }}
              >
                KID
              </Typography>
              <Box sx={{ p: 0.5 }}>
                <Typography variant="body1">{transaction.kid}</Typography>
              </Box>
            </Grid>
          )}

          {transaction.payToAccount && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{
                  color: "text.secondary",
                }}
              >
                {/* Not "Pay To Account": on an incoming row this holds the
                    account the money came *from*. The bank's own column is
                    neutrally named "Motkontonummer". */}
                Counter-account
              </Typography>
              <Box sx={{ p: 0.5 }}>
                {transfer ? (
                  <>
                    <Typography variant="body1">
                      {transfer.from} → {transfer.to}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {transaction.payToAccount} · transfer between your own
                      accounts
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body1">
                    {transaction.payToAccount}
                  </Typography>
                )}
              </Box>
            </Grid>
          )}

          {transaction.reference && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{
                  color: "text.secondary",
                }}
              >
                Reference
              </Typography>
              <Box sx={{ p: 0.5 }}>
                <Typography variant="body1">{transaction.reference}</Typography>
              </Box>
            </Grid>
          )}

          {/* An importer without invoice support has no "None" worth showing,
              but its rows still get linked — by an invoice's rule, or by
              linking the import — and then the invoice is what to show. */}
          {(invoiceSupported || linkedInvoice) && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{
                  color: "text.secondary",
                }}
              >
                Invoice
              </Typography>
              <Box
                sx={{
                  p: 0.5,
                  borderRadius: 1,
                  bgcolor: invoiceChanged ? "action.selected" : undefined,
                }}
              >
                <LinkedInvoice invoiceId={linkedInvoice} />
              </Box>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                color: "text.secondary",
              }}
            >
              Receipt
            </Typography>
            {renderField("receiptId")}
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                color: "text.secondary",
              }}
            >
              Categories
            </Typography>
            {renderField("categories")}
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                color: "text.secondary",
              }}
            >
              Origin
            </Typography>
            <Box sx={{ p: 0.5 }}>
              <Typography variant="body1">
                {transaction.origin.type}
                {transaction.origin.type === "Import" && (
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      ml: 1,
                    }}
                  >
                    ({transaction.origin.value})
                  </Typography>
                )}
              </Typography>
            </Box>
          </Grid>

          {transaction.attachments && transaction.attachments.length > 0 && (
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{
                  color: "text.secondary",
                }}
              >
                Attachments
              </Typography>
              <Box sx={{ p: 0.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
                {transaction.attachments.map((attachmentId) => {
                  const attachmentMetadata = attachmentsList?.find(
                    (a) => a.id === attachmentId,
                  );
                  if (!attachmentMetadata) return null;
                  return (
                    <AttachmentPreview
                      key={attachmentId}
                      attachment={attachmentMetadata}
                    />
                  );
                })}
              </Box>
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                color: "text.secondary",
              }}
            >
              Applied Rules
            </Typography>
            <Box sx={{ p: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {transaction.appliedRules &&
              transaction.appliedRules.length > 0 ? (
                transaction.appliedRules.map((rule) => (
                  <Tooltip
                    key={rule.id}
                    title={
                      rule.broken
                        ? "This rule is broken or deleted, so it no longer applies. Reset the transaction to clear the reference."
                        : ""
                    }
                  >
                    {/* A recorded invoice rule links to its invoice — the rule
                        itself is generated, so the invoice is what there is to
                        open. Not once the invoice is gone. */}
                    {rule.invoice && !rule.broken ? (
                      <Chip
                        label={rule.name}
                        size="small"
                        variant="outlined"
                        clickable
                        component={Link}
                        to={invoicePath(rule.invoice)}
                      />
                    ) : (
                      <Chip
                        label={rule.name}
                        size="small"
                        variant="outlined"
                        color={rule.broken ? "error" : "default"}
                        icon={
                          rule.broken ? (
                            <WarningAmberIcon fontSize="small" />
                          ) : undefined
                        }
                      />
                    )}
                  </Tooltip>
                ))
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  None
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                color: "text.secondary",
              }}
            >
              Meta
            </Typography>
            <CopyableText
              text={transaction.id}
              label="Transaction ID"
              maxLength={7}
              typographyProps={{
                variant: "caption",
                sx: { display: "inline" },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                mt: 0.5,
              }}
            >
              Added: <DateTimeDisplay date={transaction.dateAdded} />
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
              }}
            >
              Last Updated: <DateTimeDisplay date={transaction.lastUpdated} />{" "}
              (Rev {transaction.revision})
            </Typography>
          </Grid>
        </Grid>
      </Paper>
      <Typography
        variant="h5"
        component="h2"
        gutterBottom
        sx={{
          fontWeight: "bold",
        }}
      >
        History
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        {/* Dense padding and a wrapping date column keep all three columns on
            screen at phone width; without it "Changes" scrolls out of view. */}
        <Table
          aria-label="history table"
          size="small"
          sx={{ "& .MuiTableCell-root": { px: { xs: 1, sm: 2 } } }}
        >
          <TableHead sx={{ bgcolor: "action.hover" }}>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Changes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pendingActions.length > 0 && (
              <TableRow sx={{ bgcolor: "info.main", opacity: 0.1 }}>
                <TableCell colSpan={3} sx={{ p: 0 }} />
              </TableRow>
            )}
            {pendingActions.map((action, idx) => {
              const ruleName = pendingRuleName(action);
              const label =
                ruleName !== null ? `Apply Rule: ${ruleName}` : "Manual Edit";
              return (
                <TableRow key={`pending-${idx}`} sx={{ bgcolor: "info.light" }}>
                  <TableCell
                    sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "info.contrastText",
                        fontStyle: "italic",
                      }}
                    >
                      Pending
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{ verticalAlign: "top", fontWeight: "medium" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "info.contrastText" }}
                    >
                      {label}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "top" }}>
                    {ruleName !== null ? (
                      <Typography
                        variant="caption"
                        sx={{ color: "info.contrastText" }}
                      >
                        {action.field === "invoiceRuleApplied"
                          ? "Recorded as applied; the edits it makes follow"
                          : "Applies predefined actions"}
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "baseline",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "bold",
                            color: "info.contrastText",
                          }}
                        >
                          {action.field}:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "info.contrastText",
                            fontWeight: "medium",
                          }}
                        >
                          {renderActionValue(action, entityName)}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {pendingActions.length > 0 && (
              <TableRow sx={{ bgcolor: "info.main", opacity: 0.1 }}>
                <TableCell colSpan={3} sx={{ p: 0 }} />
              </TableRow>
            )}
            {history.map((entry, idx) => (
              <TableRow key={idx} hover>
                <TableCell sx={{ verticalAlign: "top" }}>
                  <DateTimeDisplay date={entry.date} wrap />
                </TableCell>
                <TableCell sx={{ verticalAlign: "top", fontWeight: "medium" }}>
                  {entry.error ? (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      sx={{ color: "error.main", alignItems: "center" }}
                    >
                      <WarningAmberIcon fontSize="small" />
                      <span>{entry.description}</span>
                    </Stack>
                  ) : (
                    entry.description
                  )}
                </TableCell>
                <TableCell sx={{ verticalAlign: "top" }}>
                  {entry.changes.length > 0 ? (
                    <Stack spacing={0.5}>
                      {entry.changes.map((change, cIdx) => (
                        <Box
                          key={cIdx}
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            alignItems: "baseline",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: "bold",
                              minWidth: 80,
                            }}
                          >
                            {change.field}:
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            {change.oldValue !== null && (
                              <Typography
                                variant="body2"
                                sx={{
                                  textDecoration: "line-through",
                                  color: "error.main",
                                }}
                              >
                                {historyValue(change.field, change.oldValue)}
                              </Typography>
                            )}
                            {change.oldValue !== null &&
                              change.newValue !== null && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "text.secondary",
                                  }}
                                >
                                  →
                                </Typography>
                              )}
                            {change.newValue !== null && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "success.main",
                                  fontWeight: "medium",
                                }}
                              >
                                {historyValue(change.field, change.newValue)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontStyle: "italic",
                      }}
                    >
                      No explicit field changes
                    </Typography>
                  )}
                  {entry.error && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "error.main",
                        fontStyle: "italic",
                        display: "block",
                        mt: 0.5,
                      }}
                    >
                      {entry.error}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {history.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontStyle: "italic",
                      py: 2,
                    }}
                  >
                    No history available for this transaction
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        open={isResetDialogOpen}
        onClose={() => setIsResetDialogOpen(false)}
      >
        <DialogTitle>Reset Transaction?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reset this transaction? This will remove
            all edits and splits, reverting it to its original state. This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsResetDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleReset}
            color="error"
            variant="contained"
            disabled={resetTransaction.isPending}
          >
            {resetTransaction.isPending ? "Resetting..." : "Reset"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isApplyRuleDialogOpen}
        onClose={() => setIsApplyRuleDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Apply Auto Transaction Rule</DialogTitle>
        <DialogContent sx={{ pt: 2, minHeight: 200 }}>
          <Autocomplete
            options={rules || []}
            getOptionLabel={(option) => option.name}
            onChange={(_, newValue) => {
              if (newValue) {
                handleApplyRule(newValue);
                setIsApplyRuleDialogOpen(false);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Auto Transaction Rule"
                variant="outlined"
                autoFocus
              />
            )}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsApplyRuleDialogOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <PromptRuleDialog
        open={pendingPromptRule !== null}
        rule={pendingPromptRule}
        transaction={effectiveTx ?? null}
        onCancel={() => setPendingPromptRule(null)}
        onConfirm={(actions) => {
          appendActions(actions);
          setPendingPromptRule(null);
        }}
      />
      <ConfirmDialog
        open={isConfirmSaveDialogOpen}
        title="Confirm Save"
        onCancel={() => setIsConfirmSaveDialogOpen(false)}
        onConfirm={confirmSave}
        confirmText={
          updateTransactions.isPending ? "Saving..." : "Save Changes"
        }
        confirmColor="primary"
        message={
          <Box>
            {updateTransactions.isError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>Saving failed</AlertTitle>
                {errorText(updateTransactions.error)}
              </Alert>
            )}
            <Typography variant="body1" gutterBottom>
              You are about to save the following sequence of changes:
            </Typography>
            {previewProblems.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>One rule can't decide what to do</AlertTitle>
                {previewProblems.map((problem, idx) => (
                  <Typography variant="body2" key={idx}>
                    {problem.rule ? `${problem.rule.name} has` : "There is"} no
                    arm for {FIELD_LABELS[problem.field].toLowerCase()}{" "}
                    <strong>“{problem.value}”</strong>. Saving is still
                    possible; that part of the rule writes nothing.
                  </Typography>
                ))}
              </Alert>
            )}
            <Box component="ol" sx={{ pl: 2 }}>
              {pendingActions.map((action, i) => {
                const ruleName = pendingRuleName(action);
                return (
                  <Typography
                    component="li"
                    key={i}
                    variant="body2"
                    sx={{ mb: 0.5 }}
                  >
                    {ruleName !== null ? (
                      <strong>Apply Auto Transaction: {ruleName}</strong>
                    ) : (
                      <>
                        Edit <strong>{action.field}</strong> to{" "}
                        <em>{renderActionValue(action, entityName)}</em>
                      </>
                    )}
                  </Typography>
                );
              })}
            </Box>
          </Box>
        }
      />
    </Box>
  );
};
